import * as Yup from "yup";
import { memoize } from "lodash";
import { FormBox } from "../../../../../utils/forms/components/FormBoxes";

export const schemaBasedBox = memoize(({ schema, required }): FormBox => {
    const sorted = Object.keys(schema.properties).sort(
        (a, b) =>
            schema.properties[a]["keyOrder"] - schema.properties[b]["keyOrder"]
    );
    return {
        title: schema.label,
        fields: sorted.map(fieldKey => {
            let field = { ...schema.properties[fieldKey] };
            field.required = required.includes(schema.label.toLowerCase());
            return createField(fieldKey, field, schema.denominations);
        }),
        columnClass: "col-12"
    };
});

const createField = (key, field, denominations) => {
       const fieldTypes = {
        integer: "number",
        boolean: "switch",
        number: "number",
        string: "input",
        array: "bulletsSelector"
    };
    return {
        name: key,
        fieldType: !field.hasOwnProperty("enum")
            ? fieldTypes[field.type]
            : field.type == "array"
            ? "bulletsSelector"
            : "select",
        fieldOptions: {
            id: key,
            placeholder:
                field.hasOwnProperty("enum") && field.type !== "array"
                    ? `Choose ${field.label}`
                    : field.label,
            noValueLabel:
                field.hasOwnProperty("enum") && field.type !== "array"
                    ? `Choose ${field.label}`
                    : field.label,
            label: field.label,
            items:
                (key === 'coin' || key === 'coins')
                    ? denominations
                    : field.hasOwnProperty("enum")
                    ? matchValues(field.enum, field.enumVal)
                    : null,
            completeList:
                (key === 'coin' || key === 'coins')
                    ? denominations
                    : field.hasOwnProperty("enum")
                    ? matchValues(field.enum, field.enumVal)
                    : null,
            bundlesList:
                (key === 'coin' || key === 'coins')
                    ? denominations
                    : field.hasOwnProperty("enum")
                    ? matchValues(field.enum, field.enumVal)
                    : null,
            exclusive: field.hasOwnProperty("exclusive")
                ? field.exclusive
                : false
        },
        validation: createFieldValidation(field),
        columnClass: field.type == "array" ? "col-12" : "col-6"
    };
};

const matchValues = (values, labels) => {
    let items = [];
    for (let i = 0; i < values.length; i++) {
        items.push({
            value: values[i],
            label: labels[i]
        });
    }

    return items;
};

const createFieldValidation = field => {
    let validator;
    if (validatorsMap[field.type]) {
        validator = validatorsMap[field.type](validator);
    }

    const isConvert = field.hasOwnProperty("convert");
    for (let att in field) {
        if (
            att !== "type" &&
            att !== "convert" &&
            att !== "lessThan" &&
            att !== "exclusiveLessThan" &&
            att !== "moreThan" &&
            att !== "exclusiveMoreThan" &&
            validatorsMap.hasOwnProperty(att) &&
            !!att
        ) {
            if (
                att !== "required" ||
                (att === "required" && field[att] !== false)
            ) {
                if (
                    (att == "maximum" ||
                        att == "minimum" ||
                        att == "exclusiveMinimum") &&
                    isConvert
                ) {
                    validator = validatorsMap[att](validator, field[att] / 100);
                } else if (att == "lte" || att == "lt") {
                    validator = validatorsMap[att](
                        validator,
                        field[att],
                        field.hasOwnProperty("maximum")
                            ? field["maximum"]
                            : field.hasOwnProperty("lessThan")
                            ? field["lessThan"]
                            : undefined
                    );
                } else if (att == "gte" || att == "gt") {
                    validator = validatorsMap[att](
                        validator,
                        field[att],
                        field.hasOwnProperty("minimum")
                            ? field["minimum"]
                            : field.hasOwnProperty("moreThan")
                            ? field["moreThan"]
                            : undefined
                    );
                } else {
                    validator = validatorsMap[att](validator, field[att]);
                }
            }
        }
    }

    return validator;
};

const validatorsMap = {
    string: () => Yup.string().nullable(),
    number: () => Yup.number().nullable().typeError("Must be an number"),
    integer: () =>
        Yup.number()
            .integer("Must be an integer")
            .nullable()
            .typeError("Must be an integer"),
    array: () => Yup.array(),
    boolean: () => Yup.boolean(),
    minLength: (validator, minLen: number) =>
        validator.min(minLen, `Min. ${minLen} character(s).`),
    required: validator => {
        return validator.required("Field is required.");
    },
    maxLength: (validator, maxLen) =>
        (validator = validator.max(maxLen, `Max. ${maxLen} character(s).`)),
    pattern: (validator, pattern) =>
        validator.matches(new RegExp(pattern), `Invalid value.`),
    maximum: (validator, maxValue) =>
        validator.max(maxValue, `Must be <= than ${maxValue}`),
    minimum: (validator, minValue) =>
        validator.min(minValue, `Must be >= than ${minValue}`),
    exclusiveMinimum: (validator, excMin) =>
        validator.moreThan(excMin, `Must be > than ${excMin}`),
    lte: (validator, field, defaultValue = 4294967295) =>
        validator.when(field.key, (limit, schema) => {
            return limit
                ? schema.max(limit, `Must be <= than ${field.label}`)
                : schema.max(defaultValue, `Must be <= than ${defaultValue}`);
        }),
    gte: (validator, field, defaultValue = 0) =>
        validator.when(field.key, (limit, schema) =>
            limit
                ? schema.min(limit, `Must be >= than ${field.label}`)
                : schema.min(defaultValue, `Must be >= than ${defaultValue}`)
        ),
    gt: (validator, field, defaultValue = 0) =>
        validator.when(field.key, (limit, schema) =>
            limit ? schema.moreThan(limit) : schema.moreThan(defaultValue)
        ),
    lt: (validator, field, defaultValue = 4294967295) =>
        validator.when(field.key, (limit, schema) =>
            limit ? schema.lessThan(limit) : schema.lessThan(defaultValue)
        ),
    email: () => Yup.string().email("e-mail is not valid")
};
